/**
 * Destino al que se vuelve tras iniciar sesión (UAT 12.1 e).
 *
 * Cuando una ruta privada manda al login, la ruta y la query originales viajan
 * en UN solo parámetro, `volver`. Antes el redirect conservaba la query pero
 * perdía la ruta, y tras el login se aterrizaba en el listado de pacientes.
 *
 * `volver` lo controla quien construya el enlace, así que es entrada no fiable
 * y el punto típico de un open redirect. Todo valor pasa por
 * `sanitizarDestinoVolver` antes de usarse, tanto al escribirlo como al leerlo.
 *
 * Módulo puro y sin dependencias de React ni de `next/*`: lo importan el
 * middleware (Edge), el cliente de API y la página de login.
 */

export const PARAMETRO_VOLVER = 'volver';
export const RUTA_LOGIN = '/app/login';
export const LONGITUD_MAXIMA_DESTINO = 2000;

const PREFIJO_APP = '/app/';
const ORIGEN_FICTICIO = 'http://destino.invalid';
const RONDAS_DE_DECODIFICACION = 3;

// Páginas de autenticación: volver a ellas tras entrar crearía un bucle.
const RUTAS_DE_AUTENTICACION = ['/app/login', '/app/register'];

// Caracteres de control: el navegador los descarta al navegar, de modo que
// `/\t/evil.example` se convierte en `//evil.example`.
const CARACTERES_DE_CONTROL = /[\u0000-\u001f\u007f]/;

function decodificarHastaEstabilizar(ruta: string): string[] | null {
    const formas = [ruta];

    for (let ronda = 0; ronda < RONDAS_DE_DECODIFICACION; ronda++) {
        try {
            const siguiente = decodeURIComponent(formas[formas.length - 1]);
            if (siguiente === formas[formas.length - 1]) {
                return formas;
            }
            formas.push(siguiente);
        } catch {
            // Una codificación malformada no es una ruta que la app haya escrito.
            return null;
        }
    }

    return formas;
}

function esRutaDeAutenticacion(ruta: string): boolean {
    const minuscula = ruta.toLowerCase();

    return RUTAS_DE_AUTENTICACION.some(
        (autenticacion) => minuscula === autenticacion || minuscula.startsWith(`${autenticacion}/`)
    );
}

// Cada forma decodificada se revisa por separado: lo que el cliente ve como
// `%2561` puede acabar siendo `a` cuando el servidor o el router decodifican.
function rutaEsSegura(pathname: string): boolean {
    const formas = decodificarHastaEstabilizar(pathname);
    if (!formas) {
        return false;
    }

    return formas.every(
        (forma) =>
            !forma.includes('\\') &&
            !forma.includes('//') &&
            !CARACTERES_DE_CONTROL.test(forma) &&
            !esRutaDeAutenticacion(forma)
    );
}

/**
 * Devuelve el destino normalizado o `null` si no es una ruta relativa de la app.
 *
 * Se devuelve la forma normalizada (`pathname + search + hash` de `URL`), no el
 * texto recibido: lo que se valida es exactamente lo que luego se navega, y los
 * dot-segments (`/app/../x`) ya vienen resueltos.
 */
export function sanitizarDestinoVolver(valor: string | null | undefined): string | null {
    if (typeof valor !== 'string' || valor.length === 0 || valor.length > LONGITUD_MAXIMA_DESTINO) {
        return null;
    }

    if (!valor.startsWith(PREFIJO_APP) || valor.includes('\\') || CARACTERES_DE_CONTROL.test(valor)) {
        return null;
    }

    let analizada: URL;
    try {
        analizada = new URL(valor, ORIGEN_FICTICIO);
    } catch {
        return null;
    }

    if (analizada.origin !== ORIGEN_FICTICIO || !analizada.pathname.startsWith(PREFIJO_APP)) {
        return null;
    }

    if (!rutaEsSegura(analizada.pathname)) {
        return null;
    }

    const normalizado = `${analizada.pathname}${analizada.search}${analizada.hash}`;

    return normalizado.length <= LONGITUD_MAXIMA_DESTINO ? normalizado : null;
}

/** Lee y valida `volver` de una query (`?a=b` o `a=b`). */
export function destinoDesdeBusqueda(busqueda: string | null | undefined): string | null {
    const valor = new URLSearchParams(busqueda ?? '').get(PARAMETRO_VOLVER);

    return sanitizarDestinoVolver(valor);
}

/**
 * Compone la URL de login que recuerda la ruta y la query de origen.
 *
 * `extras` son parámetros propios del login (p. ej. `reason`): van aparte, nunca
 * mezclados con la query original, que sólo viaja dentro de `volver`. Si el
 * origen no es un destino válido (o es demasiado largo) se devuelve el login a
 * secas: perder el enlace es mejor que romper el redirect.
 */
export function urlDeLoginConRetorno(
    pathname: string,
    busqueda: string | null | undefined,
    extras: Record<string, string> = {}
): string {
    const parametros = new URLSearchParams(extras);
    const consulta = (busqueda ?? '').replace(/^\?/, '');
    const origen = sanitizarDestinoVolver(consulta ? `${pathname}?${consulta}` : pathname);

    if (origen) {
        parametros.set(PARAMETRO_VOLVER, origen);
    }

    const cola = parametros.toString();

    return cola ? `${RUTA_LOGIN}?${cola}` : RUTA_LOGIN;
}

/** Igual que `urlDeLoginConRetorno`, tomando la pantalla actual del navegador. */
export function urlDeLoginDesdeNavegador(extras: Record<string, string> = {}): string {
    if (typeof window === 'undefined') {
        return urlDeLoginConRetorno('', '', extras);
    }

    return urlDeLoginConRetorno(window.location.pathname, window.location.search ?? '', extras);
}
